/**
 * @file SelfSubmissionVenueSuggestionInput component
 *
 * Will show suggested result as you type
 */

import { debounce } from 'lodash-es';
import Request from '../../../utils/request';
import Data from '../../../utils/dom/data';
import EventHandler from '../../../utils/dom/event-handler';
import Manipulator from '../../../utils/dom/manipulator';
import BaseComponent from '../../BaseComponent';

const NAME = 'SelfSubmissionVenueSuggestionInput';

const DATA_KEY = 'lta.selfSubmissionVenueSuggestionInput';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;

const SELECTOR_DATA_TOGGLE = '[data-lta-selfSubmissionVenueSuggestionInput]';

export default class SelfSubmissionVenueSuggestionInput extends BaseComponent {
	constructor(element) {
		super(element);

		this.state = {
			typing: false,
			inputLength: 3,
			selectedResults: {}
		};
		this._dataFetchInProgress = false;

		this.suggestResults();
	}

	static get NAME() {
		return NAME;
	}

	// Getters
	static get DATA_KEY() {
		return DATA_KEY;
	}

	dispose() {
		super.dispose();
		// Remove event handlers, reset constructor public settings etc.
	}

	suggestResults() {
		if (this._element !== null) {
			const settings = Manipulator.getDataAttributes(this._element);

			const closeButton = this._element.querySelector('.js-lta-text-ss-venue-input--close');
			const resultWrapper = this._element.querySelector('.lta-results-ss-venue-wrapper');
			const resultList = this._element.querySelector('.lta-suggest-ss-venue-results ul');
			const itemClass = 'lta-suggest-ss-venue-results--item';
			const input = closeButton.previousElementSibling;
			const inputWrapper = this._element.querySelector('#autosuggest-ss-venue-input');

			input.addEventListener('input', (event) => {
				this.state.typing = event.target.value.trim() !== '' && event.target.value.length >= this.state.inputLength;

				if (this.state.typing) {
					closeButton.classList.remove('hide-btn');
					resultWrapper.classList.add('show-results');
				} else {
					closeButton.classList.add('hide-btn');
					resultWrapper.classList.remove('show-results');
				}
			});

			const createItemMarkup = (item, index) => {
				const { postalcode } = item.address;
				const listElement = document.createElement('li');
				const button = document.createElement('button');
				const br = document.createElement('br');
				const firstLine = document.createTextNode(item.name);
				const secondLine = document.createTextNode(`${postalcode}`);

				button.setAttribute('class', `${itemClass} u-pl-3 u-pt-1 u-pb-1`);
				button.setAttribute('type', 'button');
				button.setAttribute('role', 'option');
				button.setAttribute('id', `suggestion-${index + 1}`);
				button.dataset.venueid = item.id;
				button.append(firstLine);
				button.append(br);
				button.append(secondLine);
				listElement.append(button);
				return listElement;
			};

			const dataRequest = async () => {
				// execute only if characters length >= of specific number
				if (input.value.length < this.state.inputLength) {
					this._dataFetchInProgress = false;

					return;
				}

				if (this._element.dataset.endpoint) {
					const { endpoint, endpointkey } = this._element.dataset;
					const data = await Request.get(endpoint, { [endpointkey]: input.value.trim() });
					const { venueDetails } = data;
					resultList.innerHTML = '';

					const filteredVenues =
						settings.filterPrevious && venueDetails.length > 0
							? venueDetails.filter((venue) => this.state.selectedResults[venue.id] === undefined)
							: venueDetails;

					if (filteredVenues.length > 0) {
						resultWrapper.classList.remove('has-no-results');
					} else {
						resultWrapper.classList.add('has-no-results');
					}

					for (const [index, venue] of filteredVenues.entries()) {
						resultList.append(createItemMarkup(venue, index));
					}
					inputWrapper.setAttribute('aria-expanded', 'true');
					const itemButtons = resultList.querySelectorAll(`.${itemClass}`);
					if (itemButtons.length > 0) {
						for (const button of itemButtons)
							button.addEventListener('click', (event) => {
								const { venueid } = event.currentTarget.dataset;
								const venueData = venueDetails.find((venue) => venue.id === venueid);
								const customEvent = new CustomEvent('venueSelect', { bubbles: true, detail: venueData });
								this.state.selectedResults[venueid] = venueData;
								this._element.dispatchEvent(customEvent);
								resultWrapper.classList.remove('show-results');
							});
					}

					this._dataFetchInProgress = false;
				}
			};

			input.addEventListener('keydown', () => {
				resultList.textContent = '';

				debounce(() => {
					if (!this._dataFetchInProgress) {
						this._dataFetchInProgress = true;
						dataRequest();
					}
				}, 800)();
			});

			closeButton.addEventListener('click', () => {
				input.value = '';
				resultList.textContent = '';
				closeButton.classList.add('hide-btn');
				resultWrapper.classList.remove('show-results');
				inputWrapper.setAttribute('aria-expanded', 'false');
				input.focus();
				this.state.typing = false;
			});

			document.addEventListener('click', (event) => {
				const isClickInside = this._element.contains(event.target);
				if (!isClickInside) {
					resultWrapper.classList.remove('show-results');
					inputWrapper.setAttribute('aria-expanded', 'false');
				}
			});
		}
	}

	clearSelectedResults() {
		this.state.selectedResults = {};
	}

	clearSelectedResult(id) {
		delete this.state.selectedResults[id];
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	// eslint-disable-next-line no-invalid-this
	Data.getData(this, DATA_KEY) || new SelfSubmissionVenueSuggestionInput(this);
});
